import { useEffect, useRef, useState, useMemo } from "react";
import { scoreTown }        from "../lib/scoring.js";
import { scoreBand }        from "../lib/robustness.js";
import { SfeerLeven }       from "./SfeerLeven.jsx";
import { ScoreExplainer }   from "./ScoreExplainer.jsx";
import { ExpatLeefbaarheid } from "./ExpatLeefbaarheid.jsx";
import { FarmPotential }     from "./FarmPotential.jsx";
import { TemperatureChart }  from "./TemperatureChart.jsx";
import { Climate2050Card }  from "./Climate2050Card.jsx";
import { DemographicsCard } from "./DemographicsCard.jsx";
import { SentimentCard }    from "./SentimentCard.jsx";
import { TownLinks }        from "./TownLinks.jsx";
import { TownNotes }        from "./TownNotes.jsx";
import { EmigratieChecklist } from "./EmigratieChecklist.jsx";
import { OnderwijsPanel }   from "./OnderwijsPanel.jsx";
import { RegioInfo }       from "./RegioInfo.jsx";
import { VerdictCard, HardCriteria } from "./DecisionLayer.jsx";
import { ZwolleCompare }            from "./ZwolleCompare.jsx";

const TABS = [
  { id: "wonen",  label: "🏡 Hier wonen?" },
  { id: "bezoek", label: "🧭 Hierheen?" },
  { id: "naslag", label: "📚 Naslag" },
];

function SectionTitle({ children }) {
  return <div style={{ fontSize:13, fontWeight:600, color:"#555", margin:"18px 0 8px" }}>{children}</div>;
}

export function TownModal({
  town, concelho, weights, enforceVeto, waterVeto = false,
  listings = [], onSelectListing, onClose, favorites, onToggleFav, canEdit = true, currentUser, inCompare = false, onToggleCompare,
}) {
  const overlayRef = useRef(null);
  const [tab, setTab] = useState("wonen");

  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  if (!town) return null;

  const scored   = weights ? scoreTown(town, weights, concelho, enforceVeto, { waterVeto }) : null;
  const scoreNum = scored?.pct != null ? Math.round(scored.pct) : "?";
  const vetoes   = scored?.vetoes ?? [];
  const band = useMemo(() => (weights ? scoreBand(town, weights, concelho) : null), [town, weights, concelho]);
  const cl       = concelho?.climate ?? {};
  const hr       = town.hardRequirements ?? {};
  const tags     = Array.isArray(town.vibe?.tags) ? town.vibe.tags : [];

  const scoreBg    = scored?.excluded ? "#fde8e8" : scoreNum >= 70 ? "#e8f5e9" : scoreNum >= 50 ? "#fff9e6" : "#f5f5f5";
  const scoreColor = scored?.excluded ? "#c0392b" : scoreNum >= 70 ? "#2e7d32" : scoreNum >= 50 ? "#e65100" : "#555";

  const isFav   = favorites?.has?.(town.name) ?? false;
  const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(town.name + " Portugal")}`;
  const shareUrl = `${window.location.origin}${window.location.pathname}?plaats=${encodeURIComponent(town.name)}&rol=viewer`;
  const idaSlug = (town.concelho || town.name.toLowerCase()).replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  const idaBase = town.idealista_url || `https://www.idealista.pt/comprar-casas/${idaSlug}/`;
  const idealistaUrl = idaBase.replace(/\/?$/, "/") + (/com-moradias/.test(idaBase) ? "" : "com-moradias/");
  const sharePlace = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${town.name} — Portugal Verkenner`, url: shareUrl });
      else { await navigator.clipboard.writeText(shareUrl); alert("Viewer-link gekopieerd:\n" + shareUrl); }
    } catch {}
  };

  const townListings = listings
    .filter(l => l.concelho === town.concelho || l.town === town.name)
    .slice(0, 8);

  const ListingRow = (l) => (
    <div key={l.url} onClick={() => onSelectListing?.(l)}
      style={{ padding:"8px 12px", background:"#faf8f4", borderRadius:8,
               border:"1px solid #e8e0d0", cursor:"pointer", fontSize:13, marginBottom:6 }}>
      <span style={{fontWeight:600}}>{l.price_eur ? `€${(l.price_eur/1000).toFixed(0)}k` : "Prijs onbekend"}</span>
      {l.area_total_m2 && <span style={{color:"#888",marginLeft:8}}>{l.area_total_m2} m²</span>}
      {l.bedrooms && <span style={{color:"#888",marginLeft:8}}>{l.bedrooms} slpk</span>}
      {l.title && <div style={{color:"#666",fontSize:12,marginTop:2}}>{l.title}</div>}
    </div>
  );

  return (
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position:"fixed", inset:0, zIndex:600, background:"rgba(0,0,0,.55)",
               display:"flex", alignItems:"flex-end", justifyContent:"center" }}
    >
      <div style={{
        position:"relative",
        background:"#fff", width:"100%", maxWidth:700,
        height:"92vh", borderRadius:"18px 18px 0 0",
        overflow:"hidden", display:"flex", flexDirection:"column",
      }}>

        {/* Zwevende sluitknop (Airbnb-stijl) */}
        <button onClick={onClose} aria-label="Sluiten"
          style={{
            position:"absolute", top:"calc(12px + env(safe-area-inset-top,0px))", right:14, zIndex:30,
            width:36, height:36, borderRadius:"50%", border:"none",
            background:"rgba(255,255,255,.95)", color:"#222", fontSize:20, lineHeight:1,
            cursor:"pointer", boxShadow:"0 2px 8px rgba(0,0,0,.25)",
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>×</button>

        {/* ── Scrollbaar gebied ── */}
        <div style={{ flex:1, overflowY:"auto", overscrollBehavior:"contain", WebkitOverflowScrolling:"touch" }}>

          {/* Foto-hero */}
          <SfeerLeven town={town} concelho={concelho} wikiPage={town.vibe?.wikiPage} />

          {/* Sticky header */}
          <div style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"12px 16px 8px", borderBottom:"1px solid #eee",
            position:"sticky", top:0, background:"#fff", zIndex:10,
          }}>
            <div>
              <h2 style={{ margin:0, fontSize:20, fontWeight:700 }}>{town.name}</h2>
              <div style={{ fontSize:12, color:"#888", marginTop:2 }}>
                {town.region}
                {scored?.excluded && <span style={{ color:"#c0392b", marginLeft:6 }}>⛔ uitgesloten</span>}
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              {onToggleCompare && (
                <button onClick={() => onToggleCompare(town.name)}
                  title="Aan vergelijking toevoegen"
                  style={{ border: inCompare ? "none" : "1px solid #ddd",
                           background: inCompare ? "#222" : "#fff", color: inCompare ? "#fff" : "#444",
                           borderRadius:18, padding:"7px 12px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  ⚖{inCompare ? " ✓" : ""}
                </button>
              )}
              <div style={{ background:scoreBg, color:scoreColor, borderRadius:"50%", width:46, height:46,
                display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:15 }}>{scoreNum}</div>
            </div>
          </div>

          {cl.note && /benadering/i.test(cl.note) && (
            <div style={{ margin:"6px 16px 0", padding:"6px 11px", background:"#fff8ec",
                          border:"1px solid #f0e0bf", borderRadius:8, fontSize:11, color:"#8a6d1f" }}>
              ⚠ Klimaatdata voor deze gemeente is een regionale benadering — verifieer per perceel.
            </div>
          )}

          {/* Beslis-laag */}
          {weights && (
            <div style={{ padding:"12px 16px 4px" }}>
              <VerdictCard town={town} concelho={concelho} weights={weights} />
              <HardCriteria town={town} concelho={concelho} />
            </div>
          )}

          {/* Veto-redenen */}
          {vetoes.length > 0 && (
            <div style={{ margin:"4px 16px 0", padding:"8px 12px", background:"#fff3f3",
                          border:"1px solid #fcc", borderRadius:8, fontSize:12, color:"#c0392b" }}>
              <strong>⚠ Strikte criteria overschreden:</strong>
              <ul style={{ margin:"4px 0 0 16px", padding:0 }}>
                {vetoes.map((v,i) => <li key={i}>{v}</li>)}
              </ul>
            </div>
          )}

          {/* Quick facts strip */}
          {(cl.annual_sunshine_h || cl.annual_rainfall_mm || hr.seaDistanceMin != null
            || cl.heat_days_35 != null || concelho?.fire_risk_class != null) && (
            <div style={{ display:"flex", gap:18, padding:"10px 16px", fontSize:13,
                          flexWrap:"wrap", borderBottom:"1px solid #f0f0f0", background:"#fafafa" }}>
              {hr.seaDistanceMin != null && <div style={{textAlign:"center",minWidth:50}}><div>🏄 {hr.seaDistanceMin} min</div><div style={{fontSize:11,color:"#999"}}>zee</div></div>}
              {cl.annual_sunshine_h != null && <div style={{textAlign:"center",minWidth:50}}><div>☀️ {cl.annual_sunshine_h.toLocaleString()} h</div><div style={{fontSize:11,color:"#999"}}>zon/jr</div></div>}
              {cl.annual_rainfall_mm != null && <div style={{textAlign:"center",minWidth:50}}><div>🌧 {cl.annual_rainfall_mm} mm</div><div style={{fontSize:11,color:"#999"}}>neerslag</div></div>}
              {cl.heat_days_35 != null && <div style={{textAlign:"center",minWidth:50}}><div>🌡 {cl.heat_days_35} d</div><div style={{fontSize:11,color:"#999"}}>hitte &gt;35°</div></div>}
              {concelho?.fire_risk_class != null && <div style={{textAlign:"center",minWidth:50}}><div>🔥 {concelho.fire_risk_class}/5</div><div style={{fontSize:11,color:"#999"}}>brandrisico</div></div>}
            </div>
          )}

          {/* Tabs */}
          <div style={{ display:"flex", borderBottom:"2px solid #eee", padding:"0 8px",
                        position:"sticky", top:57, background:"#fff", zIndex:9, overflowX:"auto" }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{
                  padding:"10px 14px", border:"none", background:"none", cursor:"pointer",
                  fontSize:13, fontWeight: tab === t.id ? 700 : 400,
                  color: tab === t.id ? "#2d5a8e" : "#666",
                  borderBottom: tab === t.id ? "3px solid #2d5a8e" : "3px solid transparent",
                  whiteSpace:"nowrap", marginBottom:-2,
                }}>{t.label}</button>
            ))}
          </div>

          {/* Tab-inhoud */}
          <div style={{ padding:"4px 16px 16px" }}>

            {/* HIER WONEN? */}
            {tab === "wonen" && (
              <div>
                {weights && (<><SectionTitle>Score per criterium</SectionTitle><ScoreExplainer towns={[town]} weights={weights} /></>)}
                <ExpatLeefbaarheid concelho={concelho} />
                <FarmPotential town={town} concelho={concelho} />
                {concelho?.climate && (<><SectionTitle>Klimaat vs. Zwolle</SectionTitle><ZwolleCompare concelho={concelho} /></>)}
                <Climate2050Card concelho={concelho} />
                <DemographicsCard facts={concelho} />
                <OnderwijsPanel town={town} />
                <RegioInfo town={town} />
                <SectionTitle>Snelle links</SectionTitle>
                <TownLinks town={town} />
                {townListings.length > 0 && (
                  <>
                    <SectionTitle>🏠 Vastgoed in deze regio</SectionTitle>
                    {townListings.map(ListingRow)}
                  </>
                )}
              </div>
            )}

            {/* HIERHEEN? */}
            {tab === "bezoek" && (
              <div>
                {cl.monthly_temp_max && cl.monthly_temp_min && (
                  <>
                    <SectionTitle>Maandtemperatuur</SectionTitle>
                    <TemperatureChart monthly_max={cl.monthly_temp_max} monthly_min={cl.monthly_temp_min} label={town.name} />
                  </>
                )}
                {tags.length > 0 && (
                  <>
                    <SectionTitle>Sfeer</SectionTitle>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {tags.map(tag => (
                        <span key={tag} style={{ background:"#f0ede8", color:"#5a4a36",
                          borderRadius:20, padding:"2px 10px", fontSize:12, fontWeight:500 }}>{tag}</span>
                      ))}
                    </div>
                  </>
                )}
                <SectionTitle>Lokaal nieuws & sentiment</SectionTitle>
                <SentimentCard concelho={concelho} />
                {townListings.length > 0 && (
                  <>
                    <SectionTitle>🏠 Te bezichtigen tijdens je bezoek</SectionTitle>
                    {townListings.map(ListingRow)}
                  </>
                )}
              </div>
            )}

            {/* NASLAG */}
            {tab === "naslag" && (
              <div>
                <EmigratieChecklist />
                <TownNotes townName={town.name} readOnly={!canEdit} currentUser={currentUser} />
              </div>
            )}
          </div>
        </div>

        {/* ── Sticky actiebalk ── */}
        <div style={{ flexShrink:0, display:"flex", gap:8, padding:"10px 16px calc(10px + env(safe-area-inset-bottom,0px))",
                      borderTop:"1px solid #eee", background:"#fff" }}>
          <button
            onClick={() => onToggleFav?.(town.name)}
            style={{ flex:1, height:42, borderRadius:22, border:"1px solid #ddd", background:isFav ? "#fff4e6" : "#fff",
                     color:isFav ? "#b8761f" : "#444", fontSize:14, fontWeight:600, cursor:"pointer" }}
          >{isFav ? "★ Bewaard" : "☆ Bewaren"}</button>
          <button onClick={sharePlace}
            style={{ flex:1, height:42, borderRadius:22, border:"1px solid #ddd", background:"#fff",
                     color:"#444", fontSize:14, fontWeight:600, cursor:"pointer" }}
          >🔗 Deel</button>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
            style={{ flex:1, height:42, borderRadius:22, border:"1px solid #ddd", background:"#fff",
                     color:"#444", fontSize:14, fontWeight:600, cursor:"pointer", display:"flex",
                     alignItems:"center", justifyContent:"center", textDecoration:"none" }}
          >📍 Route</a>
          <a href={idealistaUrl} target="_blank" rel="noopener noreferrer"
            style={{ flex:1, height:42, borderRadius:22, border:"none", background:"#d4537e",
                     color:"#fff", fontSize:14, fontWeight:600, cursor:"pointer", textDecoration:"none",
                     display:"flex", alignItems:"center", justifyContent:"center" }}
          >🏠 Huizen</a>
        </div>

      </div>
    </div>
  );
}
